import { TerraDraw } from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";
import {
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawLineStringMode,
  TerraDrawSelectMode,
  TerraDrawRenderMode,
} from "terra-draw";

export class TerraDrawManager {
  constructor(options = {}) {
    this.map = null;
    this.terraDraw = null;
    this.drawingMode = options.drawingMode || null;
    this.drawingControl =
      options.drawingControl !== undefined ? options.drawingControl : true;
    this.drawingControlOptions = options.drawingControlOptions || {};
    this.polygonOptions = options.polygonOptions || {};
    this.rectangleOptions = options.rectangleOptions || {};
    this.polylineOptions = options.polylineOptions || {};

    this.listeners = {};
    this.controlDiv = null;
    this.currentFeatureId = null;
    this.isInitializing = false;
    this.isCurrentlyDrawing = false;
  }

  /**
   * Set the map instance and initialize Terra Draw
   */
  setMap(map) {
    if (this.terraDraw) {
      try {
        this.terraDraw.stop();
      } catch (e) {
        console.warn("Error stopping Terra Draw:", e);
      }
      this.terraDraw = null;
    }

    this.map = map;
    this.isInitializing = false;

    if (map) {
      console.log("TerraDrawManager.setMap called");

      const isMapReady =
        map.getBounds() !== undefined && map.getProjection() !== undefined;

      if (isMapReady) {
        console.log(
          "Map is already ready, initializing Terra Draw immediately..."
        );
        this._initializeTerraDraw();
      } else {
        console.log("Map not ready yet, waiting for tilesloaded event...");
        window.google.maps.event.addListenerOnce(map, "tilesloaded", () => {
          console.log(
            "Map tilesloaded event fired, initializing Terra Draw..."
          );
          this._initializeTerraDraw();
        });
      }
    }
  }

  /**
   * Initialize Terra Draw (called after map is ready)
   */
  _initializeTerraDraw() {
    // Prevent multiple simultaneous initialization attempts
    if (!this.map || this.terraDraw || this.isInitializing) {
      console.log(
        "Skipping initialization - already initializing or initialized"
      );
      return;
    }

    this.isInitializing = true;

    try {
      console.log("Initializing Terra Draw after map is ready...");
      const mapDiv = this.map.getDiv();
      if (!mapDiv.id) {
        mapDiv.id = "terra-draw-map-" + Date.now();
        console.log("Added ID to map div:", mapDiv.id);
      }

      const adapter = new TerraDrawGoogleMapsAdapter({
        map: this.map,
        lib: window.google.maps,
      });

      this.overlayReadyCount = 0;
      this.expectedReadyCallbacks = 0;

      const modes = [];

      modes.push(
        new TerraDrawSelectMode({
          flags: {
            arbitary: {
              feature: {},
            },
          },
          onReady: () => {
            this._onModeReady("select");
          },
        })
      );

      modes.push(
        new TerraDrawRenderMode({
          modeName: "render",
          styles: this._getStylesConfig(),
          onReady: () => {
            this._onModeReady("render");
          },
        })
      );

      const drawingModes = this.drawingControlOptions.drawingModes || [
        "polygon",
        "rectangle",
        "polyline",
      ];

      if (drawingModes.includes("polygon")) {
        this.expectedReadyCallbacks++;
        modes.push(
          new TerraDrawPolygonMode({
            styles: this._getPolygonStyles(),
            pointerDistance: 10,
            onReady: () => {
              this._onModeReady("polygon");
            },
          })
        );
      }

      if (drawingModes.includes("rectangle")) {
        this.expectedReadyCallbacks++;
        modes.push(
          new TerraDrawRectangleMode({
            styles: this._getRectangleStyles(),
            onReady: () => {
              this._onModeReady("rectangle");
            },
          })
        );
      }

      if (drawingModes.includes("polyline")) {
        this.expectedReadyCallbacks++;
        modes.push(
          new TerraDrawLineStringMode({
            styles: this._getPolylineStyles(),
            pointerDistance: 10,
            onReady: () => {
              this._onModeReady("linestring");
            },
          })
        );
      }

      this.terraDraw = new TerraDraw({
        adapter: adapter,
        modes: modes,
      });

      setTimeout(() => {
        if (!this.terraDraw) return;

        this.terraDraw.start();

        if (this.drawingMode) {
          this.setDrawingMode(this.drawingMode);
        } else {
          this.terraDraw.setMode("select");
        }

        this.terraDraw.on("finish", (id) => {
          this._handleFinish(id);
        });

        this.terraDraw.on("change", (ids, type) => {
          this._handleChange(ids, type);
        });

        if (this.drawingControl) {
          this.addDrawingControls();
        }

        console.log("Terra Draw started successfully");
        this.isInitializing = false;
      }, 100);
    } catch (error) {
      console.error("Error initializing Terra Draw:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
      });
      this.terraDraw = null;
      this.isInitializing = false;
    }
  }

  _onModeReady(modeName) {
    this.overlayReadyCount++;
    console.log(
      `Mode "${modeName}" is ready (${this.overlayReadyCount}/${
        this.expectedReadyCallbacks + 2
      })`
    );

    if (this.overlayReadyCount === this.expectedReadyCallbacks + 2) {
      console.log("All Terra Draw modes are ready!");
    }
  }

  setDrawingMode(mode) {
    this.drawingMode = mode;

    if (!this.terraDraw) return;

    if (mode === null) {
      this.terraDraw.setMode("select");
    } else if (mode === "polygon") {
      this.terraDraw.setMode("polygon");
    } else if (mode === "rectangle") {
      this.terraDraw.setMode("rectangle");
    } else if (mode === "polyline") {
      this.terraDraw.setMode("linestring");
    }
  }

  setOptions(options) {
    if (options.drawingControl !== undefined) {
      this.drawingControl = options.drawingControl;
      if (this.map) {
        if (this.drawingControl) {
          this.addDrawingControls();
        } else {
          this.removeDrawingControls();
        }
      }
    }

    if (options.polygonOptions) this.polygonOptions = options.polygonOptions;
    if (options.rectangleOptions)
      this.rectangleOptions = options.rectangleOptions;
    if (options.polylineOptions) this.polylineOptions = options.polylineOptions;
  }

  addListener(eventName, callback) {
    if (!this.listeners[eventName]) {
      this.listeners[eventName] = [];
    }
    this.listeners[eventName].push(callback);
  }

  trigger(eventName, data) {
    if (this.listeners[eventName]) {
      this.listeners[eventName].forEach((callback) => callback(data));
    }
  }

  _handleChange(ids, type) {
    if (!this.terraDraw) return;

    const snapshot = this.terraDraw.getSnapshot();
    const hasDrawingFeature = snapshot.some(
      (feature) =>
        feature.properties && feature.properties.currentlyDrawing === true
    );

    this.isCurrentlyDrawing = hasDrawingFeature;
  }

  getIsDrawing() {
    return this.isCurrentlyDrawing;
  }

  _handleFinish(id) {
    const snapshot = this.terraDraw.getSnapshot();
    const feature = snapshot.find((f) => f.id === id);

    if (!feature) return;

    this.isCurrentlyDrawing = false;

    const overlay = this._createOverlayFromFeature(feature);

    let type = "polygon";
    if (feature.geometry.type === "Polygon") {
      if (feature.properties.mode === "rectangle") {
        type = "rectangle";
      } else {
        type = "polygon";
      }
    } else if (feature.geometry.type === "LineString") {
      type = "polyline";
    }

    if (overlay) overlay.cassie_shape_type = type;

    this.trigger("overlaycomplete", {
      type: type,
      overlay: overlay,
    });

    if (this.terraDraw) {
      try {
        this.terraDraw.removeFeatures([id]);
      } catch (e) {
        console.warn("Error removing feature from Terra Draw:", e);
      }
    }
  }

  /**
   * Drops a ring's repeated closing coordinate, when it has one.
   * @param {Array<Array<Number>>} ring - Ring as [lng, lat] pairs.
   * @returns {Array<Array<Number>>} The ring without its closing coordinate.
   */
  _toOpenRing(ring) {
    if (!Array.isArray(ring) || ring.length < 2) return ring;

    const first = ring[0];
    const last = ring[ring.length - 1];

    return first[0] === last[0] && first[1] === last[1]
      ? ring.slice(0, -1)
      : ring;
  }

  _createOverlayFromFeature(feature) {
    const geometry = feature.geometry;
    let overlay = null;

    if (geometry.type === "LineString") {
      const coords = geometry.coordinates.map(
        (coord) => new window.google.maps.LatLng(coord[1], coord[0])
      );

      overlay = new window.google.maps.Polyline({
        path: coords,
        strokeColor: this.polylineOptions.strokeColor || "#00B3A1",
        strokeOpacity: this.polylineOptions.strokeOpacity || 1,
        strokeWeight: this.polylineOptions.strokeWeight || 2.75,
        geodesic: this.polylineOptions.geodesic || true,
      });
    } else if (geometry.type === "Polygon") {
      const coords = this._toOpenRing(geometry.coordinates[0]).map(
        (coord) => new window.google.maps.LatLng(coord[1], coord[0])
      );

      const isRectangle = feature.properties.mode === "rectangle";

      if (isRectangle) {
        const bounds = new window.google.maps.LatLngBounds();
        coords.forEach((coord) => bounds.extend(coord));

        overlay = new window.google.maps.Rectangle({
          bounds: bounds,
          fillColor: this.rectangleOptions.fillColor || "#00B3A1",
          fillOpacity:
            this.rectangleOptions.fillOpacity !== undefined
              ? this.rectangleOptions.fillOpacity
              : 0.2,
          strokeColor: this.rectangleOptions.strokeColor || "#003832",
          strokeOpacity: this.rectangleOptions.strokeOpacity || 1,
          strokeWeight: this.rectangleOptions.strokeWeight || 2,
        });
      } else {
        overlay = new window.google.maps.Polygon({
          paths: coords,
          fillColor: this.polygonOptions.fillColor || "#00B3A1",
          fillOpacity:
            this.polygonOptions.fillOpacity !== undefined
              ? this.polygonOptions.fillOpacity
              : 0.2,
          strokeColor: this.polygonOptions.strokeColor || "#003832",
          strokeOpacity: this.polygonOptions.strokeOpacity || 1,
          strokeWeight: this.polygonOptions.strokeWeight || 2,
        });
      }
    }

    if (overlay) {
      overlay.setMap(this.map);
    }

    return overlay;
  }

  addDrawingControls() {
    if (this.controlDiv || !this.map) return;

    const controlDiv = document.createElement("div");
    controlDiv.style.backgroundColor = "#fff";
    controlDiv.style.border = "2px solid #fff";
    controlDiv.style.borderRadius = "3px";
    controlDiv.style.boxShadow = "0 2px 6px rgba(0,0,0,.3)";
    controlDiv.style.cursor = "pointer";
    controlDiv.style.marginTop = "10px";
    controlDiv.style.textAlign = "center";
    controlDiv.style.display = "flex";
    controlDiv.style.gap = "0";

    const modes = this.drawingControlOptions.drawingModes || [
      "polygon",
      "rectangle",
      "polyline",
    ];
    const position =
      this.drawingControlOptions.position ||
      window.google.maps.ControlPosition.TOP_CENTER;

    modes.forEach((mode, index) => {
      const button = this.createControlButton(mode);
      if (index === modes.length - 1) {
        button.style.borderRight = "none";
      }
      controlDiv.appendChild(button);
    });

    this.map.controls[position].push(controlDiv);
    this.controlDiv = controlDiv;
  }

  createControlButton(mode) {
    const button = document.createElement("div");
    button.style.borderRight = "1px solid #ccc";
    button.style.padding = "10px";
    button.style.cursor = "pointer";
    button.style.fontSize = "14px";
    button.style.fontFamily = "Roboto, Arial, sans-serif";

    const modeNames = {
      polygon: () => {
        const svg = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "svg"
        );
        svg.setAttribute("width", "18");
        svg.setAttribute("height", "18");
        svg.setAttribute("viewBox", "0 0 18 18"); // reduzido

        const path = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "path"
        );
        path.setAttribute("d", "M9 1 L17 6 L14 17 H4 L1 6 Z"); // coordenadas dentro de 18x18
        path.setAttribute("stroke", "black");
        path.setAttribute("fill", "none");
        path.setAttribute("stroke-width", "2");

        svg.appendChild(path);
        return svg;
      },

      rectangle: () => {
        const svg = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "svg"
        );
        svg.setAttribute("width", "18");
        svg.setAttribute("height", "18");
        svg.setAttribute("viewBox", "0 0 18 18");

        const rect = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "rect"
        );
        rect.setAttribute("x", "2");
        rect.setAttribute("y", "2");
        rect.setAttribute("width", "14");
        rect.setAttribute("height", "14");
        rect.setAttribute("stroke", "black");
        rect.setAttribute("fill", "none");
        rect.setAttribute("stroke-width", "2");

        svg.appendChild(rect);
        return svg;
      },

      polyline: () => {
        const svg = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "svg"
        );
        svg.setAttribute("width", "18");
        svg.setAttribute("height", "18");
        svg.setAttribute("viewBox", "0 0 18 18");

        const line = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "line"
        );
        line.setAttribute("x1", "2");
        line.setAttribute("y1", "16");
        line.setAttribute("x2", "16");
        line.setAttribute("y2", "2");
        line.setAttribute("stroke", "black");
        line.setAttribute("stroke-width", "2");

        svg.appendChild(line);
        return svg;
      },
    };

    button.appendChild(modeNames[mode]() || document.createTextNode(mode));
    button.title = `Desenhar ${modeNames[mode] || mode}`;

    button.addEventListener("click", () => {
      if (this.drawingMode === mode) {
        this.setDrawingMode(null);
        button.style.backgroundColor = "#fff";
      } else {
        Array.from(button.parentElement.children).forEach((btn) => {
          btn.style.backgroundColor = "#fff";
        });

        this.setDrawingMode(mode);
        button.style.backgroundColor = "#e6e6e6";
      }
      this.trigger("modechange", this.drawingMode);
    });

    button.addEventListener("mouseenter", () => {
      if (this.drawingMode !== mode) {
        button.style.backgroundColor = "#f0f0f0";
      }
    });

    button.addEventListener("mouseleave", () => {
      if (this.drawingMode !== mode) {
        button.style.backgroundColor = "#fff";
      }
    });

    return button;
  }

  removeDrawingControls() {
    if (this.controlDiv && this.map) {
      const position =
        this.drawingControlOptions.position ||
        window.google.maps.ControlPosition.TOP_CENTER;
      const controls = this.map.controls[position];
      const index = controls.getArray().indexOf(this.controlDiv);
      if (index > -1) {
        controls.removeAt(index);
      }
      this.controlDiv = null;
    }
  }

  cleanup() {
    console.log("Cleaning up TerraDrawManager...");
    this.removeDrawingControls();
    if (this.terraDraw) {
      try {
        this.terraDraw.stop();
        this.terraDraw.clear();
      } catch (e) {
        console.warn("Error during Terra Draw cleanup:", e);
      }
      this.terraDraw = null;
    }
    this.listeners = {};
    this.map = null;
    this.isInitializing = false;
    console.log("TerraDrawManager cleanup complete");
  }

  _getStylesConfig() {
    return {
      polygonFillColor: this.polygonOptions.fillColor || "#00B3A1",
      polygonOutlineColor: this.polygonOptions.strokeColor || "#003832",
      polygonFillOpacity: this.polygonOptions.fillOpacity || 0.2,
      polygonOutlineWidth: this.polygonOptions.strokeWeight || 0.5,
    };
  }

  _getPolygonStyles() {
    return {
      fillColor: this.polygonOptions.fillColor || "#00B3A1",
      outlineColor: this.polygonOptions.strokeColor || "#003832",
      fillOpacity:
        this.polygonOptions.fillOpacity !== undefined
          ? this.polygonOptions.fillOpacity
          : 0.2,
      outlineWidth: this.polygonOptions.strokeWeight || 2,
      closingPointColor: this.polygonOptions.strokeColor || "#003832",
      closingPointWidth: 2,
      closingPointOutlineWidth: 2,
    };
  }

  _getRectangleStyles() {
    return {
      fillColor: this.rectangleOptions.fillColor || "#00B3A1",
      outlineColor: this.rectangleOptions.strokeColor || "#003832",
      fillOpacity:
        this.rectangleOptions.fillOpacity !== undefined
          ? this.rectangleOptions.fillOpacity
          : 0.2,
      outlineWidth: this.rectangleOptions.strokeWeight || 2,
      pointColor: this.rectangleOptions.strokeColor || "#003832",
      pointWidth: 4,
      pointOutlineColor: "#FFFFFF",
      pointOutlineWidth: 2,
    };
  }

  _getPolylineStyles() {
    return {
      lineStringColor: this.polylineOptions.strokeColor || "#00B3A1",
      lineStringWidth: this.polylineOptions.strokeWeight || 2.75,
      closingPointColor: this.polylineOptions.strokeColor || "#00B3A1",
      closingPointWidth: 2,
      closingPointOutlineWidth: 2,
    };
  }
}

export const clearInstanceListeners = (drawingManager) => {
  if (drawingManager && drawingManager.cleanup) {
    drawingManager.cleanup();
  }
};
